// src/components/CostBreakdown.js
// ───────────────────────────────────────────────────────────────────────────
// Desglose de costo por COMPONENTE de una pieza — ÚNICA fuente de verdad, compartida por el
// cotizador interno (QuoteView, 1 pieza) y el multipieza (ProductQuoteView, N piezas). Misma
// FORMA (etiquetas, orden, campos, formato $) en ambos; difieren solo en FUNCIÓN (cuántas piezas).
// Regla reusar-no-duplicar: las etiquetas y los campos del motor (material/corte/impresión/tinta/
// cuchilla/costoTotal/sheets) viven acá una sola vez. NO calcula nada: solo presenta lo que ya
// devolvió el servidor (computeCost / computeProductCost exponen los mismos campos por pieza).
// ───────────────────────────────────────────────────────────────────────────
import React from 'react';

const money = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

// Orden y etiquetas de los componentes de costo (clave = campo que devuelve el motor por pieza).
// 3.ª columna = gate: 'cut' va con el toggle Corte (CORTE+CUCHILLA), 'print' con Impresión
// (IMPRESIÓN+TINTA). El motor ya devuelve esos cubos en 0 cuando el toggle está OFF; acá solo
// se OCULTAN de la lista (auditoría = cubos aplicables). Sin flag (estimados viejos) → se listan.
const COMPONENTES = [
  ['MATERIAL', 'material', null],
  ['CORTE', 'corte', 'cut'],
  ['IMPRESIÓN', 'impresion', 'print'],
  ['TINTA', 'tinta', 'print'],
  ['CUCHILLA', 'cuchilla', 'cut'],
];

/**
 * Filas <tr> del desglose por componente de UNA pieza (cubos aplicables + costo de lote · hojas).
 * Se inserta dentro de un <tbody> propio de cada vista, así cada cotizador conserva su envoltorio
 * (acordeón, encabezado de pieza, extras) pero comparte EXACTAMENTE estas filas.
 * @param {object} piece  { material, corte, impresion, tinta, cuchilla, costoTotal, sheets, cutEnabled?, printEnabled? }
 * @param {string} totalLabel  etiqueta de la fila de total (interno: "Costo total"; pieza: "Costo pieza (lote)")
 */
export function CostComponentRows({ piece, totalLabel = 'Costo total' }) {
  if (!piece) return null;
  const aplica = (gate) => gate === null
    || (gate === 'cut' ? piece.cutEnabled !== false : piece.printEnabled !== false);
  return (
    <>
      {COMPONENTES.filter(([, , gate]) => aplica(gate)).map(([label, key]) => (
        <tr key={key}>
          <td className="py-0.5 text-gray-500">{label}</td>
          <td className="py-0.5 text-right tabular-nums">{money(piece[key])}</td>
        </tr>
      ))}
      <tr className="font-medium">
        <td className="py-0.5 text-gray-600">{totalLabel}{piece.sheets != null ? ` · ${piece.sheets}h` : ''}</td>
        <td className="py-0.5 text-right tabular-nums">{money(piece.costoTotal)}</td>
      </tr>
    </>
  );
}

/**
 * Tabla completa (envoltorio + filas) para UNA pieza. La usa el cotizador interno, que muestra
 * el desglose de su única pieza. El multipieza usa <CostComponentRows> directo (una tabla por pieza).
 */
export function CostBreakdownTable({ piece, totalLabel }) {
  return (
    <table className="w-full text-sm mt-2">
      <tbody className="divide-y divide-gray-100">
        <CostComponentRows piece={piece} totalLabel={totalLabel} />
      </tbody>
    </table>
  );
}
